import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LayoutGrid, List, AlarmClock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageTitle, Panel, StatoBadge, MatchBadge, STATI } from "@/components/studio/common";
import { api, eur, d } from "@/lib/api";

const Stat = ({ label, value, testid, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
    <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`font-serif text-3xl mt-2 ${accent || "text-navy"}`}>{value}</p>
  </div>
);

const Card = ({ p, onClick }) => (
  <button onClick={onClick} data-testid={`kanban-card-${p.id}`} className="w-full text-left bg-white border border-slate-200 rounded-lg p-3 hover:border-navy hover:shadow-sm transition-all">
    <div className="flex justify-between gap-2"><span className="font-mono text-[11px] text-slate-500">{p.numero}</span><MatchBadge m={p.match} /></div>
    <p className="font-medium text-navy text-sm mt-1.5">{p.cliente.cognome} {p.cliente.nome}</p>
    <p className="text-xs text-slate-500 mt-0.5">{eur(p.importo_effettivo ?? p.importo_stimato)}</p>
    {(p.flags?.revoca || p.flags?.bypass) && <p className="text-[11px] text-red-700 mt-1 font-semibold">{p.flags.revoca ? "Revoca" : "By-pass"}</p>}
  </button>
);

const Kanban = ({ rows, go }) => (
  <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pratiche-kanban">
    {STATI.map(([k, l]) => {
      const col = rows.filter((r) => r.status === k);
      return (
        <div key={k} className="w-64 shrink-0 bg-slate-100/70 rounded-xl p-3" data-testid={`kanban-col-${k}`}>
          <div className="flex justify-between items-center mb-3 px-1"><StatoBadge s={k} /><span className="text-xs text-slate-500">{col.length}</span></div>
          <div className="space-y-2">{col.map((p) => <Card key={p.id} p={p} onClick={() => go(p.id)} />)}</div>
        </div>
      );
    })}
  </div>
);

const Table = ({ rows, go }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="pratiche-table">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>
        {["Numero", "Cliente", "C.F.", "Stato", "Matching", "Importo", "Compenso", "Creata"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}
      </tr></thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p.id} data-testid={`pratica-row-${p.id}`} onClick={() => go(p.id)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
            <td className="px-4 py-3 font-mono text-xs">{p.numero}</td>
            <td className="px-4 py-3 font-medium text-navy">{p.cliente.cognome} {p.cliente.nome}</td>
            <td className="px-4 py-3 font-mono text-xs">{p.cliente.codice_fiscale}</td>
            <td className="px-4 py-3"><StatoBadge s={p.status} /></td>
            <td className="px-4 py-3"><MatchBadge m={p.match} /></td>
            <td className="px-4 py-3">{eur(p.importo_effettivo ?? p.importo_stimato)}</td>
            <td className="px-4 py-3">{eur(p.compenso)}</td>
            <td className="px-4 py-3 text-xs text-slate-500">{d(p.created_at)}</td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">Nessuna pratica</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function Dashboard() {
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [match, setMatch] = useState("all");

  useEffect(() => { api.get("/pratiche/dashboard").then((r) => setDash(r.data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/pratiche", { params: { q, match: match === "all" ? "" : match } }).then((r) => setRows(r.data)), 250);
    return () => clearTimeout(t);
  }, [q, match]);

  const go = (id) => nav(`/studio/pratiche/${id}`);
  const t = dash?.totals || {};

  return (
    <div data-testid="studio-dashboard">
      <PageTitle title="Pratiche" sub="Pipeline di recupero dalla verifica al saldo cliente" />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Stat testid="stat-lead-match" label="Lead con match" value={t.lead_match ?? "—"} accent="text-emerald-700" />
        <Stat testid="stat-da-validare" label="Mandati da validare" value={t.da_validare ?? "—"} accent="text-gold-dark" />
        <Stat testid="stat-pipeline" label="Stimato in pipeline" value={eur(t.stimato_pipeline)} />
        <Stat testid="stat-incassato" label="Incassato" value={eur(t.incassato)} />
        <Stat testid="stat-compensi" label="Compensi" value={eur(t.compensi)} />
      </div>
      {dash?.reminders?.length > 0 && (
        <Panel title="Scadenze" className="mb-8" testid="dashboard-reminders">
          <ul className="divide-y divide-slate-100">
            {dash.reminders.map((r) => (
              <li key={r.id} className="py-2.5 flex items-center gap-3 text-sm">
                <AlarmClock className={`h-4 w-4 ${r.overdue ? "text-red-600" : "text-gold-dark"}`} />
                <span className={`font-mono text-xs ${r.overdue ? "text-red-700 font-semibold" : "text-slate-500"}`}>{d(r.due_date)}</span>
                <span className="flex-1">{r.title}</span>
                <Link to={`/studio/pratiche/${r.pratica_id}`} className="text-xs text-navy underline">{r.numero} · {r.cliente}</Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <Input data-testid="pratiche-search-input" placeholder="Cerca per cognome, codice fiscale o numero pratica" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-sm bg-white" />
        <Select value={match} onValueChange={setMatch}>
          <SelectTrigger data-testid="pratiche-match-filter" className="sm:w-48 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli esiti</SelectItem><SelectItem value="found">Solo match</SelectItem>
            <SelectItem value="possible">Possibili</SelectItem><SelectItem value="not_found">Nessun match</SelectItem><SelectItem value="unavailable">Consap non raggiungibile</SelectItem>
          </SelectContent>
        </Select>
        <div className="sm:ml-auto flex gap-1 bg-white border border-slate-200 rounded-md p-1">
          <Button size="sm" variant={view === "kanban" ? "default" : "ghost"} data-testid="view-kanban-btn" onClick={() => setView("kanban")}><LayoutGrid className="h-4 w-4 mr-1" />Kanban</Button>
          <Button size="sm" variant={view === "table" ? "default" : "ghost"} data-testid="view-table-btn" onClick={() => setView("table")}><List className="h-4 w-4 mr-1" />Tabella</Button>
        </div>
      </div>
      {view === "kanban" ? <Kanban rows={rows} go={go} /> : <Table rows={rows} go={go} />}
    </div>
  );
}
